import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { localeNames } from 'shared-locales';
import { Button, ProductIdentifier } from 'components/common';
import { Box } from 'components/common/box';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import {
  Checkbox,
  Emails,
  Form,
  FormGroup,
  FormSection,
  ReminderIntervalsSelector,
  RichText,
  Select,
  Text
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import {
  PathSettingsCertificates,
  PathSettingsImage,
  PathSettingsSummaryEmail
} from '.';

import type { Path } from 'shared-types';
import type { GetCategoriesRes } from 'shared-types/responses';

type PathAttributes = Pick<
  Path,
  | 'certificateAllowDownloading'
  | 'certificateEmailList'
  | 'certificateLanguage'
  | 'certificateSendToOtherEmails'
  | 'certificateSendToUser'
  | 'customAttributes'
  | 'deadlineReminderIntervals'
  | 'defaultRemindersLanguage'
  | 'description'
  | 'picture'
  | 'picturePosition'
  | 'remindersEmailList'
  | 'requireCompletionBeforeNextCourse'
  | 'sendCourseDeadlineReminders'
  | 'sendCourseDeadlineRemindersEmailList'
  | 'summaryEmailEnabled'
  | 'summaryEmailFrequency'
  | 'summaryEmailOptions'
  | 'summaryEmailRecipients'
  | 'title'
>;

interface FormState extends PathAttributes {
  categories?: string[];
  CertificateTemplateId: number;
}

const PathSettings = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { path },
    serverConfig: { pathCustomAttributes }
  } = useReduxState();

  const uploader = useRef<any>(undefined);

  const [loading, setLoading] = useState(false);
  const [requireUpload, setRequireUpload] = useState(false);

  const { data } = useApiData<GetCategoriesRes>({
    errorMessage: messages.categoriesGetCategoriesError,
    url: '/admin/categories-paths'
  });

  const initialValues: FormState = {
    categories: path.categories || [],
    certificateAllowDownloading: path.certificateAllowDownloading,
    certificateEmailList: path.certificateEmailList,
    certificateLanguage: path.certificateLanguage || 'en-GB',
    certificateSendToOtherEmails: path.certificateSendToOtherEmails,
    certificateSendToUser: path.certificateSendToUser,
    customAttributes: path.customAttributes,
    deadlineReminderIntervals: path.deadlineReminderIntervals,
    defaultRemindersLanguage: path.defaultRemindersLanguage,
    description: path.description,
    picture: path.picture,
    picturePosition: path.picturePosition,
    remindersEmailList: path.remindersEmailList,
    requireCompletionBeforeNextCourse: path.requireCompletionBeforeNextCourse,
    sendCourseDeadlineReminders: path.sendCourseDeadlineReminders,
    sendCourseDeadlineRemindersEmailList:
      path.sendCourseDeadlineRemindersEmailList,
    summaryEmailEnabled: path.summaryEmailEnabled,
    summaryEmailFrequency: path.summaryEmailFrequency,
    summaryEmailOptions: path.summaryEmailOptions,
    summaryEmailRecipients: path.summaryEmailRecipients,
    title: path.title,
    CertificateTemplateId: path.certificateTemplateId || 0
  };

  const handleRequireUpload = (value: boolean) => setRequireUpload(value);

  const handleSubmit = async (values: FormState) => {
    try {
      setLoading(true);

      if (requireUpload) {
        const result = await uploader.current.upload();
        if (result) values.picture = result.url;
      }

      const { categories, ...pathSettings } = values;

      const response = await api.put(`/admin/paths/${path.id}`, {
        ...pathSettings,
        categories: categories || [],
        CertificateTemplateId: values.CertificateTemplateId || null
      });
      if (!response.data || !response.data.success) throw new Error('error');

      const pathsResponse = await api.get(`/admin/paths?id=${path.id}`);

      setLoading(false);

      dispatch.updatePageState({
        hasUnsavedChanges: false,
        path: pathsResponse.data.paths[0]
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = async (value: string) => {
    if (!value || !value.trim()) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/paths?title=${value}&id=ne|${path.id}`);

      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <div className="settings">
      <Box title={messages.commonSettings} padding={0}>
        <Form
          allowEmptyStrings={true}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <>
              <div style={{ padding: '10px 10px 0' }}>
                <UnsavedFormChanges initialValues={initialValues} />
              </div>

              <div>
                <FormSection title={messages.siteSettingsCommon}>
                  <FormGroup htmlFor="title" label={messages.commonTitle}>
                    <Text
                      customValidateAsync={validateTitle}
                      field="title"
                      id="title"
                    />
                  </FormGroup>

                  <ProductIdentifier
                    productIdentifier={path.productIdentifier}
                  />

                  <FormGroup
                    htmlFor="description"
                    label={messages.commonDescription}
                  >
                    <RichText
                      autoGrow={false}
                      field="description"
                      id="description"
                      maxLength={3000}
                      placeholder={messages.commonDescriptionPlaceholder}
                    />
                  </FormGroup>

                  <FormGroup
                    htmlFor="categories"
                    label={messages.commonCategories}
                  >
                    <Select
                      creatable={true}
                      field="categories"
                      id="categories"
                      isClearable={true}
                      isMulti={true}
                      isSearchable={true}
                      noOptionsMessage={messages.settingsNoCategoriesFound}
                      options={
                        data?.categories.map((category) => ({
                          label: category.title,
                          value: category.title
                        })) || []
                      }
                      placeholder={messages.settingsAddCategory}
                    />
                  </FormGroup>
                </FormSection>

                <PathSettingsImage
                  picture={path.picture}
                  onFileAdded={handleRequireUpload}
                  setUploader={(fileUploader) =>
                    (uploader.current = fileUploader)
                  }
                />

                <FormSection title={messages.pathsSettingsAccessToSteps}>
                  <Checkbox
                    field="requireCompletionBeforeNextCourse"
                    id="requireCompletionBeforeNextCourse"
                    label={messages.pathsSettingsRequireStepCompletion}
                    labelHelpText={
                      messages.pathsSettingsRequireStepCompletionInfo
                    }
                  />
                </FormSection>

                <FormSection title={messages.commonEmailReminders}>
                  <Checkbox
                    field="sendCourseDeadlineReminders"
                    id="sendCourseDeadlineReminders"
                    label={messages.pathsSettingsSendReminders}
                  />
                  <Checkbox
                    field="sendCourseDeadlineRemindersEmailList"
                    id="sendCourseDeadlineRemindersEmailList"
                    label={`${messages.commonSendAdditionalEmailReminders}:`}
                    style={{ marginTop: 10 }}
                  />
                  <Emails
                    disabled={
                      !formState.values.sendCourseDeadlineRemindersEmailList
                    }
                    field="remindersEmailList"
                    id="remindersEmailList"
                    style={{ marginLeft: 20, marginTop: 8 }}
                  />

                  <FormGroup
                    label={messages.pathsSettingsDefaultRemindersLanguage}
                    labelHelpText={
                      messages.pathsSettingsDefaultRemindersLanguageTooltip
                    }
                  >
                    <Select
                      disabled={
                        !formState.values.sendCourseDeadlineReminders &&
                        !formState.values.sendCourseDeadlineRemindersEmailList
                      }
                      field="defaultRemindersLanguage"
                      id="default-reminders-language"
                      options={Object.keys(localeNames).map((localeKey) => ({
                        label: localeNames[localeKey],
                        value: localeKey
                      }))}
                      style={{ maxWidth: 200 }}
                    />
                  </FormGroup>

                  <ReminderIntervalsSelector
                    disabled={
                      !formState.values.sendCourseDeadlineReminders &&
                      !formState.values.sendCourseDeadlineRemindersEmailList
                    }
                    field="deadlineReminderIntervals"
                    type="deadline"
                  />
                </FormSection>

                <PathSettingsCertificates />

                <PathSettingsSummaryEmail />

                {pathCustomAttributes?.length ? (
                  <FormSection
                    maxWidth="100%"
                    title={messages.settingsAdditionalSettings}
                  >
                    <div className="custom-attribute-settings">
                      <ProductCustomAttributes
                        attributes={path.customAttributes || {}}
                        type="path"
                      />
                    </div>
                  </FormSection>
                ) : null}

                <FormSection>
                  <Button
                    category="success"
                    disabled={formState.invalid}
                    loading={loading}
                    text={messages.commonSave}
                    type="submit"
                  />
                </FormSection>
              </div>
            </>
          )}
        </Form>
      </Box>
    </div>
  );
};

export default PathSettings;
